import { useState, Fragment, useContext, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useFetch from '../../hooks/use-fetch.js';
import { useTranslation } from 'react-i18next';

import UserContext from '../../store/user-context';
import town from '../../assets/images/golingo-town-a.jpg';
import scaryTown from '../../assets/images/ghoulingo-town.jpg';
import xmasTown from '../../assets/images/christmas-golingo.jpg';

import LanguageSelector from '../UI/LanguageSelector';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment } from '@fortawesome/free-solid-svg-icons';
import Loader from '../UI/Loader.js';
import ErrorScreen from '../UI/ErrorScreen.js';
import ParrotIntro from '../Town/IntroScreen/ParrotIntro.js';
import DownloadCheck from '../Town/PWA/DownloadCheck.js';
import useAnalyticsEventTracker from '../../hooks/use-analytics';
import ParrotHelper from '../Town/Tutorial/ParrotHelper.js';
import { searchParamsSplitter } from '../Helpers/Util/Tutorial.js';
import Pumpkin2 from '../../assets/icons/pumpkin2.png';

import {isHalloween, isXmas} from '../Helpers/Util/SeasonalHelper';
import { useDispatch } from 'react-redux';

function addWindowOverflow() {
  document.querySelector('html').style.overflow = 'hidden';
}

function lockWindowViewport() {
  let winTmer;
  // console.log('scrolling');
  clearTimeout(winTmer);
  winTmer = setTimeout(function () {
    // console.log('stop win scrolling')
    addWindowOverflow();
  }, 300);
}

const Town = () => {
  const { t: translate } = useTranslation();
  const { apiUrl } = window['appConfig'];
  const params = useLocation();
  const dispatch = useDispatch();
  let searchParams = searchParamsSplitter(params);
  let forceTutorial = ('tutorial' in searchParams) ? searchParams.tutorial : false;
  const {
    data: locationData,
    error: fetchError,
    initFunc: fetchLocations,
  } = useFetch();
  const { data: townData, initFunc: fetchTownData } = useFetch();
  const [townLocations, setTownLocations] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [progressValue, setProgressValue] = useState({ value: [], length: 0 });
  const userContext = useContext(UserContext);
  const [showIntro, setShowIntro] = useState(true);
  const [lastLocation, setLastLocation] = useState(null);
  const [introSlides, setIntroSlides] = useState([]);
  const gaEventTracker = useAnalyticsEventTracker('Map');
  const [showTutorial, setShowTutorial] = useState(forceTutorial);
  const [tutorialCancelled, setTutorialCancelled] = useState(false);
  const [townClass, setTownClass] = useState('town');
  const townRef = useRef();
  const halloweenLocation = "House";
  const MINUTE_MS = 60000;
  const [townSrc, setTownSrc] = useState('');
  const [isSeasonal, setIsSeasonal] = useState(false);

  //Toggle overflow hidden on html tag if learningLang is null or not
  const overflowCheck = () => {
    if (userContext.user.learning === null) {
      addWindowOverflow();
    } else if (showIntro === true) {
      addWindowOverflow();
    } else {
      document.querySelector('html').style.removeProperty('overflow');
    }
  };

  const showSeasonalTown = () => {
    if (isHalloween()) {
      setTownSrc(scaryTown)
      setIsSeasonal(true);
      return true;
    }

    if (isXmas()) { 
      setTownSrc(xmasTown);
      setIsSeasonal(true);
      return true; 
    }

    setIsSeasonal(false);
    return false;
  }

  const centerMap = (centerPoint = '', behavior = '') => {
    let delay = 50;

    //Default to hotel
    let whereToStart = 'Tutorial';

    if (centerPoint !== '') {
      if (centerPoint === 'Tutorial') {
        whereToStart = 'Person'
      } else {
        whereToStart = centerPoint;
      }
    }

    // } else {
    //   if (window.innerWidth < 768) {
    //     whereToStart = 'Person';
    //   }
    // }

    let ping = document.getElementById(`${whereToStart}-ping`);

    if (ping) {
      //logic only works with timeout, assuming because DOM hasn't rendered fully?
      let scrollArgs = {
        block: 'center',
        inline: 'center',
      };

      if (behavior !== '') {
        scrollArgs['behavior'] = behavior;
      }

      setTimeout(function () {
        ping.scrollIntoView(scrollArgs);
      }, delay);
    }
  };

  useEffect(() => {
    overflowCheck();
    if (showTutorial) {
      centerMap('', 'smooth');
      if (showIntro === false) {
        setTownClass('town tutorial-active');
      }
    }
  }, [userContext.user, showIntro]);

  useEffect(() => {
    // setIsFetching(true);
    dispatch({
      type: 'resetCurrentLesson',
    });

    let mounted = true;
    if (mounted) {
      const fetchOptions = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      };

      let refDataUrl = location.hostname === "127.0.0.1" || location.hostname === "localhost" ? `${apiUrl}/app/referenceData?ignorenocontent=false` : `${apiUrl}/app/referenceData`;

      fetchLocations({
        url: refDataUrl,
        options: fetchOptions,
      });
      fetchTownData({ url: `${apiUrl}/progress/town`, options: fetchOptions });
    }

    showSeasonalTown();
    const timerInterval = setInterval(() => {
      showSeasonalTown();
    }, MINUTE_MS);

    return () => {
      mounted = false;
      setTimeout(function () {
        document.querySelector('html').style.removeProperty('overflow');
        window.removeEventListener('scroll', lockWindowViewport)
      },350)
      clearInterval(timerInterval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
    };

  }, []);

  useEffect(() => {
    if (locationData && townData) {
      if (Object.prototype.hasOwnProperty.call(locationData, 'locations')) {
        //Loading bar
        setProgressValue((prevState) => ({
          ...prevState,
          value: locationData.locations,
          length: locationData.locations.length,
        }));

        let centerPoint = townData.lastLocation;

        // if (centerPoint === '') {
        //   centerPoint = false
        // }

        setLastLocation(centerPoint);
        setTownLocations(locationData.locations);
      }
    }
  }, [locationData, townData]);

  useEffect(() => {
    if (townLocations.length > 0) {
      setTimeout(function () {
        setIsFetching(false);
      }, 1000);
    }
  }, [townLocations]);

  useEffect(() => {
    const newUserSlides = [
      // { tag: 'h2', content: 'Welcome to goLingo!' },
      {
        content:
          [translate('town-new-user-1')]
      },
    ];

    // let returnUserArr = lastLocation === 'Tutorial' ? [translate('parrot-tutorial-complete-1'), translate('parrot-tutorial-complete-2')] : [translate('town-return-user-1', { lastLocation: lastLocation }), translate('town-return-user-2')];
    let returnUserArr = lastLocation === 'Tutorial' ? [translate('parrot-tutorial-complete-1')] : [translate('town-return-user-1', { lastLocation: lastLocation })];

    const returnUserSlides = [
      // { tag: 'h2', content: 'Welcome back!' },
      {
        content: returnUserArr,
      },
    ];

    if (townLocations.length > 0) {
      if (lastLocation) {
        setIntroSlides(returnUserSlides);
      }

      if (lastLocation === '' && tutorialCancelled === false) {
        setIntroSlides(newUserSlides);
        setShowTutorial(true);
      }

      if (showTutorial || lastLocation === '') {
        let tutorialLocation = townLocations.filter(loc => loc.name.toLowerCase() === 'tutorial');
        if (tutorialLocation[0].enabled === false) {
          setIntroSlides([{ content: [translate('town-generic')] }]);
          setShowTutorial(false);
          setTutorialCancelled(true);
          return;
        }

        centerMap('', 'smooth');

        if (showIntro === false) {
          setTownClass('town tutorial-active');
        }
      } else {
        centerMap(lastLocation, 'smooth');
      }
    }
  }, [lastLocation, townLocations]);

  return (
    <Fragment>
      {userContext.user.learning === null && (
        <div className="language-modal">
          <div className="language-screen">
            <h2 className="language-screen__heading">{translate('choose-language')}</h2>
            <LanguageSelector />
          </div>
        </div>
      )}
      {fetchError && <ErrorScreen>{fetchError}</ErrorScreen>}
      {isFetching && <Loader progress={progressValue} />}
      {<DownloadCheck />}
      {showIntro && (
        <ParrotIntro
          slides={introSlides}
          setShowIntro={setShowIntro}
          lastLocation={lastLocation}
          lastLessonTime={townData?.lastCompletedLesson}
          showTutorial={showTutorial}
          tutorialCancelled={tutorialCancelled}
          townData={locationData}
        />
      )}
      <div className={townClass} ref={townRef}>
        {isSeasonal && <img className={`townImage townImage--secondary ${isSeasonal ? 'townImage--active' : ''}`} src={townSrc} alt="golingo Town" />}
        <img id="town" className={`townImage ${! isSeasonal ? 'townImage--active' : ''}`} src={town} alt="golingo Town" />
        {townLocations && (
          townLocations.filter((location) => {
            if (showTutorial && location.name !== 'Tutorial') {
              return false;
            }

            if (!showTutorial && location.name === 'Tutorial') {
              return false
            }

            return true;
          })
            .map((location, index) => {
              let percentage_val = 0;
              if (townData) {
                for (const [key, value] of Object.entries(townData.locations)) {
                  if (location.name === key) {
                    percentage_val = value.percent;
                  }
                }
              }

              let percentage = showIntro === false ? percentage_val : 0;
              let icon = location.name === halloweenLocation && isHalloween() ?  <img id="pumpkin" src={Pumpkin2}/>: <FontAwesomeIcon icon={faComment} size="1x" />;


              if (location.enabled)
                return (
                  <Fragment key={index}>
                    {
                      showTutorial && !showIntro && location.name.toLowerCase() === 'tutorial' &&
                      <ParrotHelper 
                        isActive={true} 
                        sideToShow="right" 
                        showBackground={false}
                        styleOverrides={{
                          left: parseInt(location.pin[0]) - 130 + 'px', 
                          position: 'absolute', 
                          top: window.innerWidth < 768 ? parseInt(location.pin[0]) - 400 + 'px' : parseInt(location.pin[0]) - 440 + 'px',
                          maxWidth: '350px',
                          padding: 0
                        }}
                      >
                        <h4>{translate('pollywood-tutorial-complete')}</h4>
                        {translate('click-pin')}
                      </ParrotHelper>
                    }
                    <div
                      onClick={() => {
                        gaEventTracker('Enter Location', location.name)
                      }}
                      className={`pin-container ${percentage === 100 ? 'complete' : ''} ${location.name === halloweenLocation && isHalloween() ? 'spooky' : ''}`}
                      style={{
                        left: location.pin[0] + 'px',
                        top: location.pin[1] + 'px',
                      }}
                      data-position={location.position}
                    >
                      {/* //https://dev.to/shantanu_jana/circular-progress-bar-using-html-and-css-1oda */}
                      <div className="percent-bar">
                        <div
                          className="mask full"
                          style={{
                            transform: `rotate(${(180 * percentage) / 100}deg)`,
                          }}
                        >
                          <div
                            className="fill"
                            style={{
                              transform: `rotate(${(180 * percentage) / 100}deg)`,
                            }}
                          ></div>
                        </div>
                        <div className="mask half">
                          <div
                            className="fill"
                            style={{
                              transform: `rotate(${(180 * percentage) / 100}deg)`,
                            }}
                          ></div>
                        </div>
                      </div>
                      <Link
                        key={index}
                        to={`/conversation/${location.name}`}
                        className="location-pin"
                        id={`${location.name}-ping`}
                        title={location.name}
                        pin={location.pin}
                      >
                        {icon}
                        <span className="pin-percentage">{`${percentage}%`}</span>
                      </Link>
                      <div
                        className="percent-circle"
                        style={{
                          transform: `rotate(${(360 * percentage) / 100 + 90
                            }deg) translate(41px)`,
                        }}
                      ></div>
                      <span className="ping"></span>
                    </div>
                  </Fragment>
                );
            })
        )}
      </div>
    </Fragment>
  );
};

export default Town;
